<template>
    <div class="qs">
        <div class="qs-head">
            <span class="qs-title">{{ $t(`unit.mode.mode_${mode}`) }}</span>
            <span class="qs-sub">{{ $t('unit.settings.head_note') }}</span>
        </div>

        <!-- ════ 影片 / PDF（F7）════════════════════════════════════ -->
        <template v-if="mode === 'video' || mode === 'pdf'">
            <div class="qs-field">
                <div class="qs-lbl">
                    {{ $t(`unit.settings.${mode}_label`) }}<span class="qs-req">*</span>
                </div>
                <!-- ⚠ 上傳與「使用 YouTube 影片」的入口在挑選器的 tab 列右邊，不在這裡 -->
                <MediaSlot :media="mediaOf(singleMediaId)"
                    :placeholder="$t(`unit.settings.${mode}_none`)"
                    @pick="openPicker(purposeOfThisMode, { field: singleField })"
                    @clear="patch({ [singleField]: null })"
                    @edit="openEdit(mediaOf(singleMediaId))" />
            </div>

            <!-- ⚠ 這個開關會讓畫格工作區出現/消失（規格 §7.2），所以要講出來 -->
            <div class="qs-field">
                <v-checkbox :input-value="value.require_writing" dense hide-details
                    class="qs-check"
                    @change="patch({ require_writing: $event === true })">
                    <template #label>
                        <div>
                            <div class="qs-check-lbl">{{ $t('unit.settings.require_writing') }}</div>
                            <div class="qs-check-note">{{ $t('unit.settings.require_writing_note') }}</div>
                        </div>
                    </template>
                </v-checkbox>
            </div>
        </template>

        <!-- ════ 找單字（F9）════════════════════════════════════════ -->
        <template v-else-if="mode === 'game_wordsearch'">
            <div class="qs-field">
                <div class="qs-lbl">
                    {{ $t('unit.settings.wordlist_label') }}<span class="qs-req">*</span>
                </div>
                <MediaSlot :media="mediaOf(value.game.wordlist_media_id)"
                    :placeholder="$t('unit.settings.wordlist_none')"
                    @pick="openPicker('wordsearch', { game: 'wordlist_media_id' })"
                    @clear="patchGame({ wordlist_media_id: null })"
                    @edit="openEdit(mediaOf(value.game.wordlist_media_id))"
                    @edit-words="openWordlist(mediaOf(value.game.wordlist_media_id))" />
                <!-- ⚠ 要顯示字表有幾個字：target_count 的上界就是它（規格 §8） -->
                <div v-if="wordCount > 0" class="qs-hint">
                    {{ $t('unit.settings.word_count', { n: wordCount }) }}
                </div>
            </div>

            <!-- ⚠ 難度參數是**題目設定**不是素材屬性（規格 §2.2）：
                 同一份官方字表要能讓不同年級的老師設不同難度。 -->
            <div class="qs-grid">
                <div class="qs-field">
                    <div class="qs-lbl">{{ $t('unit.settings.grid_size') }}</div>
                    <v-text-field :value="value.game.grid_size" type="number" dense outlined
                        hide-details :min="MIN_GRID_SIZE" :max="MAX_GRID_SIZE"
                        @input="patchGame({ grid_size: toInt($event) })" />
                    <div class="qs-hint">
                        {{ $t('unit.settings.grid_size_hint',
                              { min: MIN_GRID_SIZE, max: MAX_GRID_SIZE }) }}
                    </div>
                </div>
                <div class="qs-field">
                    <div class="qs-lbl">{{ $t('unit.settings.target_count') }}</div>
                    <v-text-field :value="value.game.target_count" type="number" dense outlined
                        hide-details :min="1"
                        @input="patchGame({ target_count: toInt($event) })" />
                    <div class="qs-hint">{{ $t('unit.settings.target_count_hint') }}</div>
                </div>
            </div>

            <div class="qs-field">
                <v-checkbox :input-value="value.game.is_cheat_mode" dense hide-details
                    class="qs-check" :label="$t('unit.settings.is_cheat_mode')"
                    @change="patchGame({ is_cheat_mode: $event === true })" />
            </div>
        </template>

        <!-- ════ 講顏色（F9）════════════════════════════════════════ -->
        <template v-else-if="mode === 'game_colorword'">
            <div class="qs-field">
                <div class="qs-lbl">
                    {{ $t('unit.settings.colorword_label') }}<span class="qs-req">*</span>
                </div>
                <MediaSlot :media="mediaOf(value.game.wordlist_media_id)"
                    :placeholder="$t('unit.settings.colorword_none')"
                    @pick="openPicker('colorword', { game: 'wordlist_media_id' })"
                    @clear="patchGame({ wordlist_media_id: null })"
                    @edit="openEdit(mediaOf(value.game.wordlist_media_id))"
                    @edit-words="openWordlist(mediaOf(value.game.wordlist_media_id))" />
                <div v-if="wordCount > 0" class="qs-hint">
                    {{ $t('unit.settings.word_count', { n: wordCount }) }}
                </div>
            </div>
            <div class="qs-field qs-field--narrow">
                <div class="qs-lbl">{{ $t('unit.settings.timer') }}</div>
                <v-text-field :value="value.game.timer" type="number" dense outlined hide-details
                    :min="MIN_TIMER" :max="MAX_TIMER"
                    @input="patchGame({ timer: toInt($event) })" />
                <div class="qs-hint">{{ $t('unit.settings.timer_hint') }}</div>
            </div>
        </template>

        <!-- ════ 翻卡牌（F8）════════════════════════════════════════ -->
        <template v-else-if="mode === 'game_flipcard'">
            <div class="qs-list">
                <div v-for="(card, i) in value.game.cards" :key="i" class="qs-item">
                    <div class="qs-item-head">
                        <span class="qs-item-no">{{ $t('unit.settings.card_n', { n: i + 1 }) }}</span>
                        <button type="button" class="qs-del" @click="removeCard(i)">
                            {{ $t('unit.settings.remove') }}
                        </button>
                    </div>
                    <!-- 兩面各一張圖 + 一個標題。⚠ 配對是**題目設定**不是素材屬性
                         （ADR 0004）：同一張圖可以在不同題目裡配不同的答案。 -->
                    <div class="qs-two">
                        <div>
                            <div class="qs-lbl-sm">{{ $t('unit.settings.card_front') }}</div>
                            <MediaSlot :media="mediaOf(card.front_media_id)" compact
                                :placeholder="$t('unit.settings.image_none')"
                                @pick="openPicker('image', { card: i, side: 'front_media_id' })"
                                @clear="patchCard(i, { front_media_id: null })"
                                @edit="openEdit(mediaOf(card.front_media_id))" />
                            <v-text-field :value="card.front_title" dense outlined hide-details
                                class="mt-2" :maxlength="MAX_TITLE_LENGTH"
                                :placeholder="$t('unit.settings.card_front_ph')"
                                @input="patchCard(i, { front_title: $event })" />
                        </div>
                        <div>
                            <div class="qs-lbl-sm">{{ $t('unit.settings.card_back') }}</div>
                            <MediaSlot :media="mediaOf(card.back_media_id)" compact
                                :placeholder="$t('unit.settings.image_none')"
                                @pick="openPicker('image', { card: i, side: 'back_media_id' })"
                                @clear="patchCard(i, { back_media_id: null })"
                                @edit="openEdit(mediaOf(card.back_media_id))" />
                            <v-text-field :value="card.back_title" dense outlined hide-details
                                class="mt-2" :maxlength="MAX_TITLE_LENGTH"
                                :placeholder="$t('unit.settings.card_back_ph')"
                                @input="patchCard(i, { back_title: $event })" />
                        </div>
                    </div>
                </div>
            </div>
            <button type="button" class="qs-add" :disabled="value.game.cards.length >= MAX_CARDS"
                @click="addCard">
                {{ value.game.cards.length >= MAX_CARDS
                    ? $t('unit.settings.card_max', { n: MAX_CARDS })
                    : $t('unit.settings.card_add') }}
            </button>
        </template>

        <!-- ════ 找不同（F8）════════════════════════════════════════ -->
        <template v-else-if="mode === 'game_hotspot'">
            <div class="qs-list">
                <!-- ⚠ **只有一關**（2026-09-04，上限從 10 降到 1）。學生端只會玩
                     `quests[0]`——`FindDifference.vue` 的 `currentIdx` 從來沒被改過，
                     所以多的關**靜默被丟掉**（見 `MAX_LEVELS` 的說明）。
                     ⚠ **v-for 仍然留著**：降上限之前建的題目可能有 2 關以上，
                     要讓老師看得到並移除，不能靜默隱藏。正常情況它只跑一次。
                     ⚠ 所以編號與「移除」只在**真的有多關**時才顯示——一關的時候
                     「第 1 關」是雜訊，而「移除」會把唯一一關刪掉（存檔會被擋）。 -->
                <div v-for="(level, i) in value.game.levels" :key="i" class="qs-item">
                    <div v-if="value.game.levels.length > 1" class="qs-item-head">
                        <span class="qs-item-no">{{ $t('unit.settings.level_n', { n: i + 1 }) }}</span>
                        <button type="button" class="qs-del" @click="removeLevel(i)">
                            {{ $t('unit.settings.remove') }}
                        </button>
                    </div>
                    <MediaSlot :media="mediaOf(level.hotspot_media_id)"
                        :placeholder="$t('unit.settings.hotspot_none')"
                        @pick="openPicker('hotspot', { level: i })"
                        @clear="patchLevel(i, { hotspot_media_id: null })"
                        @edit="openEdit(mediaOf(level.hotspot_media_id))"
                        @edit-hotspot="openHotspot(mediaOf(level.hotspot_media_id))" />

                    <!-- ⚠ **熱區預覽**（規格 §7.2）。老師必須看得到這張圖有幾個熱區、
                         在哪裡，否則無法判斷適不適合他的班級。
                         ⚠ **「有幾個」這個數字要留。** 學生端支援多個熱區
                         （`foundIds.size === targets.length`，還有「還剩 N 個」的訊息），
                         而官方圖實際上就有 5 個（`seed-official.sql`）。1 個和 5 個對
                         老師是完全不同的難度。老師自己上傳的目前一律是 1 個。
                         ⚠ **「不能調整」那句話只對不是自己的素材成立**（2026-09-04）：
                         自己上傳的可以從 ⋯ →「編輯熱區」改。 -->
                    <div v-if="hotspotOf(level)" class="qs-hotspot">
                        <div class="qs-hotspot-wrap">
                            <!-- ⚠ 這張 img 必須自己就是定位框（`width:100%; height:auto`）。
                                 外面包固定比例 + object-fit:contain 會讓圖留白置中，
                                 於是「容器的百分比」不等於「圖的百分比」，每個框都偏移。
                                 學生端 FindDifference.vue 用的就是 w-full h-auto。 -->
                            <img :src="hotspotOf(level).url" class="qs-hotspot-img" alt="">
                            <span v-for="t in targetsOfLevel(level)" :key="t.id"
                                class="qs-target" :style="targetStyle(t, hotspotOf(level))" />
                        </div>
                        <div class="qs-hint">
                            {{ $t('unit.settings.hotspot_count',
                                  { n: targetsOfLevel(level).length }) }}
                            <template v-if="!hotspotOf(level).is_mine">
                                —— {{ $t('unit.settings.hotspot_readonly') }}
                            </template>
                        </div>
                    </div>

                    <v-text-field :value="level.title" dense outlined hide-details class="mt-2"
                        :maxlength="MAX_TITLE_LENGTH"
                        :placeholder="$t('unit.settings.level_title_ph')"
                        @input="patchLevel(i, { title: $event })" />
                </div>
            </div>
            <!-- ⚠ **還沒有關卡時直接給一個空的素材欄**，而不是「＋ 新增一關」。
                 一關就好的話，「新增一關」那一步只是多一次點擊——找不同現在跟影片／PDF
                 一樣：一個素材欄、一個提示文字。
                 ⚠ `@pick` 走 `openPicker('hotspot', { level: 0 })`，而 `onPicked`
                 對還不存在的第 0 關會自己補一列（見 `patchLevel`）。
                 ⚠ **沒有「新增」按鈕了**（上限就是 1）。多關的舊題目靠上面的
                 v-for 顯示與移除。 -->
            <div v-if="value.game.levels.length === 0" class="qs-item">
                <MediaSlot :media="null"
                    :placeholder="$t('unit.settings.hotspot_none')"
                    @pick="openPicker('hotspot', { level: 0 })" />
            </div>
        </template>

        <!-- ⚠ 一個 picker 服務所有欄位，靠 pickerTarget 記住要塞回哪裡。
             5 張卡 × 2 面就是 10 個 picker 實例，那不必要。 -->
        <MediaPicker v-if="pickerPurpose" :show.sync="pickerOpen" :purpose="pickerPurpose"
            :value="pickerCurrentId" @select="onPicked" />
        <!-- ⚠ 一個對話框服務所有欄位，同 picker 的理由：5 張卡 × 2 面就是 10 個實例。
             改完 emit `media-loaded` 讓上層更新快照，否則標題改了但預覽還是舊的。 -->
        <MediaEditDialog :show.sync="editOpen" :media="editing"
            @saved="onEdited" />
        <!-- 字表的內容編輯器。⚠ 與 `MediaEditDialog` 是**兩個對話框**：那一個改標題／說明／
             可見範圍（一定成功），這一個改單字（**可能被後端拒絕**——改字表會影響所有引用
             它的題目，後端會重驗那些題目的 target_count 與 grid_size）。
             ⚠ 只在字表模式建出來（`v-if`），它的 `show` watcher 是 immediate 的。 -->
        <WordlistDialog v-if="isWordlistMode" :show.sync="wordlistOpen" :media="editingWordlist"
            @saved="onEdited" />
        <!-- 熱區編輯器。⚠ 只在找不同模式建出來（`v-if`），它的 `show` watcher 是
             immediate 的。⚠ 與字表編輯器不同：這一支不會因為引用它的題目而失敗。 -->
        <HotspotEditDialog v-if="mode === 'game_hotspot'" :show.sync="hotspotOpen"
            :media="editingHotspot" @saved="onEdited" />
    </div>
</template>

<script>
import MediaPicker from '@/components/console/diary/media/MediaPicker';
import MediaSlot from '@/components/console/diary/unit/MediaSlot';
import MediaEditDialog from '@/components/console/diary/media/MediaEditDialog';
import WordlistDialog from '@/components/console/diary/media/WordlistDialog';
import HotspotEditDialog from '@/components/console/diary/media/HotspotEditDialog';
import {
    MAX_CARDS, MAX_TITLE_LENGTH, MIN_GRID_SIZE, MAX_GRID_SIZE,
    MIN_TIMER, MAX_TIMER, purposeOfMode, wordsOf, targetsOf,
} from '@/components/console/diary/unit/questModeUtils';

/**
 * 模式專屬設定區（F7–F9）— 見 mangachat-studio-api docs/plans/MediaLibrary.spec.md
 * §4.2（形狀）、§7.2（哪個模式有哪些欄位）、§8（驗證）。
 *
 * ⚠ **編輯的是「儲存形狀」不是學生端形狀。** `game.cards[]` / `game.levels[]` /
 * `game.wordlist_media_id` 是資料庫裡的樣子；學生端讀的 `game.quest` 是 B9 resolver
 * 的輸出。兩者刻意不同——儲存形狀要能被驗證與編輯，學生端形狀是動不了的既有契約。
 * **看到 `cards` 而不是 `quest` 是對的，不要「修正」。**
 *
 * ⚠ **不改 prop。** 所有變更都 emit 一個新物件（`patch` / `patchGame` / …），
 * 因為 `value` 是 UnitQuestEdit 的 `modeSettings`，而那份物件同時餵給 dirty 比對的
 * baseline——原地改會讓 baseline 跟著變，於是永遠偵測不到未儲存的變更。
 */
export default {
    name: 'QuestModeSettings',
    components: {
        MediaPicker, MediaSlot, MediaEditDialog, WordlistDialog, HotspotEditDialog,
    },
    props: {
        /** 模式設定（v-model）。形狀見 questModeUtils.normalizeSettings。 */
        value: { type: Object, required: true },
        mode: { type: String, required: true },
        /** `media_id` → 素材資料。後端在題目詳情裡一次帶出來的。 */
        mediaMap: { type: Object, default: () => ({}) },
    },
    data() {
        return {
            MAX_CARDS,
            MAX_TITLE_LENGTH,
            MIN_GRID_SIZE,
            MAX_GRID_SIZE,
            MIN_TIMER,
            MAX_TIMER,
            pickerOpen: false,
            pickerPurpose: '',
            editOpen: false,
            editing: null,
            wordlistOpen: false,
            editingWordlist: null,
            hotspotOpen: false,
            editingHotspot: null,
            /// 選好之後要塞回哪個欄位。形狀見 openPicker。
            target: null,
        };
    },
    computed: {
        purposeOfThisMode() {
            return purposeOfMode(this.mode);
        },
        singleField() {
            return this.mode === 'video' ? 'video_media_id' : 'pdf_media_id';
        },
        singleMediaId() {
            return this.value[this.singleField];
        },
        /** 目前這個目標已選的 id，讓 picker 標出「已選中」。 */
        pickerCurrentId() {
            const t = this.target;
            if (!t) return null;
            if (t.field) return this.value[t.field];
            if (t.game) return this.value.game[t.game];
            /// ⚠⚠ 這兩行**必須容忍索引不存在**。找不同還沒有關卡時，空的素材欄按
            /// 「選擇素材」會走 `openPicker('hotspot', { level: 0 })`，而 `levels[0]`
            /// 還不存在——原本寫 `levels[t.level].hotspot_media_id` 會丟 TypeError，
            /// 而這是 `<MediaPicker :value>` 的 prop，於是**整個對話框算繪不出來**：
            /// 症狀是「按了沒反應」，錯誤只進 console（2026-09-09 修）。
            /// ⚠ 寫入路徑一直是對的（`patchLevel` 會自己補第 0 關），漏的是讀取路徑。
            /// 卡片那一行照樣加保護：同一類錯誤，而且一樣不會有編譯警告。
            if (t.card !== undefined) {
                const card = this.value.game.cards[t.card];
                return card ? card[t.side] : null;
            }
            if (t.level !== undefined) {
                const level = this.value.game.levels[t.level];
                return level ? level.hotspot_media_id : null;
            }
            return null;
        },
        /** 這個模式用不用字表。決定要不要把字表編輯器建出來。 */
        isWordlistMode() {
            return this.mode === 'game_wordsearch' || this.mode === 'game_colorword';
        },
        /** 字表的字數。⚠ `target_count` 的上界就是它（規格 §8）。 */
        wordCount() {
            return wordsOf(this.mediaOf(this.value.game.wordlist_media_id)).length;
        },
    },
    methods: {
        mediaOf(id) {
            return id ? (this.mediaMap[String(id)] || null) : null;
        },
        hotspotOf(level) {
            const m = this.mediaOf(level.hotspot_media_id);
            /// 沒有 url 就不畫——例如素材查不到（被硬刪）的情況
            return m && m.url ? m : null;
        },
        targetsOfLevel(level) {
            return targetsOf(this.mediaOf(level.hotspot_media_id));
        },
        /**
         * 熱區框的定位。⚠ 用**百分比**而不是 px：圖是 `width:100%` 縮放的，
         * 而座標是原始像素（規格 §2.2）。學生端也是這樣換算的。
         */
        targetStyle(t, media) {
            const w = media.width || 0;
            const h = media.height || 0;
            if (!w || !h) {
                return { display: 'none' };
            }
            return {
                left: `${(t.pxX / w) * 100}%`,
                top: `${(t.pxY / h) * 100}%`,
                width: `${(t.pxW / w) * 100}%`,
                height: `${(t.pxH / h) * 100}%`,
            };
        },

        /* ---------- emit（一律送新物件，不改 prop） ---------- */

        patch(partial) {
            this.$emit('input', Object.assign({}, this.value, partial));
        },
        patchGame(partial) {
            this.patch({ game: Object.assign({}, this.value.game, partial) });
        },
        patchCard(index, partial) {
            const cards = this.value.game.cards.map(
                (c, i) => (i === index ? Object.assign({}, c, partial) : c));
            this.patchGame({ cards });
        },
        /**
         * 改第 `index` 關。
         * ⚠ **那一關還不存在時要補一列**（2026-09-04）：找不同的初始 `levels` 是空陣列
         * （`buildInitialResources`），而現在的 UI 直接給一個空的素材欄、沒有「新增一關」
         * 的步驟——所以第一次選圖時 `index` 是 0 而 `levels` 還是空的。
         */
        patchLevel(index, partial) {
            const current = this.value.game.levels;
            if (index >= current.length) {
                /// 只補到 index（含）為止。正常情況 index 一定是 0。
                const grown = current.slice();
                while (grown.length <= index) {
                    grown.push({ hotspot_media_id: null, title: '' });
                }
                grown[index] = Object.assign({}, grown[index], partial);
                this.patchGame({ levels: grown });
                return;
            }
            const levels = current.map(
                (l, i) => (i === index ? Object.assign({}, l, partial) : l));
            this.patchGame({ levels });
        },
        addCard() {
            if (this.value.game.cards.length >= MAX_CARDS) return;
            this.patchGame({
                cards: this.value.game.cards.concat([{
                    front_media_id: null, front_title: '', back_media_id: null, back_title: '',
                }]),
            });
        },
        removeCard(index) {
            this.patchGame({ cards: this.value.game.cards.filter((c, i) => i !== index) });
        },
        /// ⚠ `addLevel` 在 2026-09-04 移除：上限降到 1 之後沒有「新增一關」的入口了，
        /// 第一關由 `patchLevel(0, ...)` 在選圖時自己補（見那一支的說明）。
        removeLevel(index) {
            this.patchGame({ levels: this.value.game.levels.filter((l, i) => i !== index) });
        },

        /* ---------- picker / upload ---------- */

        /**
         * @param {string} purpose 要挑哪一種素材
         * @param {Object} target 選好之後塞回哪裡：
         *   `{field}` 頂層欄位 / `{game}` game 底下 / `{card,side}` 第幾張卡的哪一面 /
         *   `{level}` 第幾關
         */
        openPicker(purpose, target) {
            this.target = target;
            this.pickerPurpose = purpose;
            this.pickerOpen = true;
        },
        /**
         * 挑選器選好之後的回填。
         *
         * ⚠ 也要 emit `media-loaded`：`mediaMap` 是**載入時的快照**，剛選的素材不在裡面。
         * 不補進去的話老師會看到「已選好」但預覽是空的。
         */
        onPicked(media) {
            if (!media || !media.media_id) {
                return;
            }
            this.$emit('media-loaded', media);
            const t = this.target;
            if (!t) return;
            if (t.field) {
                this.patch({ [t.field]: media.media_id });
            } else if (t.game) {
                this.patchGame({ [t.game]: media.media_id });
            } else if (t.card !== undefined) {
                this.patchCard(t.card, { [t.side]: media.media_id });
            } else if (t.level !== undefined) {
                this.patchLevel(t.level, { hotspot_media_id: media.media_id });
            }
        },

        openEdit(media) {
            if (!media || !media.is_mine) {
                return;
            }
            this.editing = media;
            this.editOpen = true;
        },

        /**
         * 開字表的內容編輯器。
         * ⚠ **只有自己的字表**：後端對官方素材與別人的素材回 403（同 openEdit）。
         *   官方字表要改就複製一份成自己的——那不是這一版要做的事。
         * ⚠ 一定要在開啟**之前**設好 `editingWordlist`：對話框的 `show` watcher 是
         *   `immediate` 的，reset 時會讀 `media`，順序反了會讀到上一次的值。
         */
        openWordlist(media) {
            if (!media || !media.is_mine) {
                return;
            }
            this.editingWordlist = media;
            this.wordlistOpen = true;
        },

        /**
         * 開熱區編輯器。
         * ⚠ **只有自己的素材**：後端對官方素材與別人的素材回 403（同 openEdit）。
         * ⚠ 一定要在開啟**之前**設好 `editingHotspot`：對話框的 `show` watcher 是
         *   `immediate` 的，reset 時會讀 `media`，順序反了會讀到上一次的值。
         */
        openHotspot(media) {
            if (!media || !media.is_mine) {
                return;
            }
            this.editingHotspot = media;
            this.hotspotOpen = true;
        },

        /** 改完把新的那一筆推回上層的 mediaMap——預覽與檔名才會跟著更新。 */
        onEdited(updated) {
            this.$emit('media-loaded', updated);
        },

        toInt(v) {
            const n = Number(v);
            return Number.isInteger(n) ? n : null;
        },
    },
};
</script>

<style scoped>
/* ⚠ **自己宣告背景與邊框，不要借父層的 `.card`。**
   在這之前這裡是 `class="qs card"`，而 `.card` 定義在 UnitQuestEdit 的 scoped style 裡
   ——它之所以會生效，是因為 Vue 2 會把父層的 scope id 也加到子元件的根節點上。
   那是實作細節，父層改個 class 名稱或把這個元件搬到別處就會整塊失去樣式。
   數值與 UnitQuestEdit 的 `.card` 保持一致。 */
.qs {
    background: #fff;
    border: 1px solid #e8e8e8;
    border-radius: 6px;
    padding: 18px 20px;
    margin-bottom: 16px;
}

.qs-head {
    display: flex;
    align-items: baseline;
    gap: 10px;
    margin-bottom: 14px;
}

.qs-title {
    font-size: 15px;
    font-weight: 600;
    color: #263238;
}

.qs-sub {
    font-size: 12px;
    color: #90a4ae;
}

.qs-field {
    margin-bottom: 14px;
}

.qs-field--narrow {
    max-width: 200px;
}

/* 找單字的兩個難度輸入（字母格大小、要找幾個字）。
   ⚠ **只有這一組用它**，所以直接綁寬度；別的地方要兩欄請用 `.qs-two`。

   ⚠ `max-width` 要與 `MediaSlot.vue` 的 `.ms` 一致（620px）——上面選字表的那一塊就是它。
   兩者對齊之後這一行才不會比素材欄還寬；不對齊的話會看起來像兩個不同的區塊。
   **改一邊要改兩邊**（兩個元件各自 scoped，共用不了同一個變數）。

   數字輸入本來就不需要這麼寬，但**刻意不再縮**：兩欄各 303px 是「與上面對齊」的結果，
   而對齊比「剛好塞得下數字」更重要——欄寬不一致在整頁看起來是壞掉的。 */
.qs-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 14px;
    max-width: 620px;
}

.qs-two {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 14px;
}

.qs-lbl {
    font-size: 13px;
    font-weight: 600;
    color: #455a64;
    margin-bottom: 6px;
}

.qs-lbl-sm {
    font-size: 12px;
    color: #607d8b;
    margin-bottom: 4px;
}

.qs-req {
    color: #e53935;
}

.qs-hint {
    font-size: 12px;
    color: #90a4ae;
    margin-top: 4px;
    line-height: 1.5;
}


.qs-check {
    margin-top: 0;
}

.qs-check-lbl {
    font-size: 13px;
    color: #263238;
}

.qs-check-note {
    font-size: 11px;
    color: #90a4ae;
}

.qs-list {
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.qs-item {
    border: 1px solid #eceff1;
    border-radius: 6px;
    padding: 12px;
}

.qs-item-head {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 8px;
}

.qs-item-no {
    font-size: 12px;
    font-weight: 600;
    color: #546e7a;
}

.qs-del {
    font-size: 12px;
    color: #d32f2f;
    background: none;
    border: none;
    padding: 0;
    cursor: pointer;
}

.qs-add {
    margin-top: 12px;
    font-size: 13px;
    color: #1e88e5;
    background: none;
    border: 1px dashed #b0bec5;
    border-radius: 6px;
    padding: 8px 14px;
    cursor: pointer;
    width: 100%;
}

.qs-add:disabled {
    color: #b0bec5;
    cursor: not-allowed;
}

.qs-hotspot {
    margin-top: 8px;
}

.qs-hotspot-wrap {
    position: relative;
    max-width: 320px;
    line-height: 0;
}

.qs-hotspot-img {
    width: 100%;
    height: auto;
    display: block;
    border-radius: 4px;
}

.qs-target {
    position: absolute;
    border: 2px solid #ff5252;
    border-radius: 3px;
    background: rgba(255, 82, 82, 0.16);
    box-sizing: border-box;
    pointer-events: none;
}

@media (max-width: 700px) {
    .qs-grid,
    .qs-two {
        grid-template-columns: 1fr;
    }
}
</style>
